import type { ComponentProps } from 'react';
import { type Stack } from 'expo-router';
import { StyleSheet } from 'react-native';

import { theme, TYPE_CONFIG } from '@sg/garnish';

// ─── Colors ─────────────────────────────────────────────────────────────────

export const APP_BACKGROUND = theme.colors.APP_BACKGROUND;

// ─── Base ────────────────────────────────────────────────────────────────────

/**
 * Base screen options shared by all Stack navigators.
 *
 * NOTE: `headerLeft` is set to prevent the default `HeaderBackButton` from
 * rendering on web. `@react-navigation/elements` v2's `HeaderBackButton`
 * uses `useLocale` which doesn't exist in `@react-navigation/native` v6.
 * Screens that need custom header-left buttons set them via `setOptions`.
 */
export const DEFAULT_SCREEN_OPTIONS = {
  headerShown: false,
  headerLeft: () => null,
  contentStyle: { backgroundColor: APP_BACKGROUND },
} satisfies ScreenOptions;

// ─── Header ──────────────────────────────────────────────────────────────────

/**
 * Screen options for Stack navigators that show native headers.
 */
export const SCREEN_HEADER_OPTIONS = {
  headerShadowVisible: false,
  headerTintColor: theme.colors.GREEN_1,
  headerStyle: { backgroundColor: APP_BACKGROUND },
  headerTitleStyle: TYPE_CONFIG.BODY['18'] as ScreenOptions['headerTitleStyle'],
  headerTitleAlign: 'center',
  headerBackVisible: false,
  headerTitle: '',
} satisfies ScreenOptions;

// ─── Styles ──────────────────────────────────────────────────────────────────

export const CONTENT_WITH_BG_COLOR_STYLES = { backgroundColor: APP_BACKGROUND };
export const CONTENT_WITHOUT_BG_STYLES = { backgroundColor: 'transparent' };

export const SLOT_CONTAINER_STYLES = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: APP_BACKGROUND,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * Screen options type derived from expo-router's `Stack` component.
 * This avoids version mismatches between the root `@react-navigation/native-stack`
 * (v6) and expo-router's bundled copy (v7).
 */
export type ScreenOptions = Extract<
  NonNullable<ComponentProps<typeof Stack>['screenOptions']>,
  Record<string, unknown>
>;

/**
 * Return type used with `satisfies` for object literal validation.
 */
export type ScreenOptionsMap = Record<string, ScreenOptions & Record<string, unknown>>;
